import React from "react";
import { PropertyDetails } from "../shared-components/ViewFragments/PropertyDetails/web";
import { PropertyDetailsPrint } from "../shared-components/ViewFragments/PropertyDetailsPrint/web";
import { decodeHashUrl } from "../shared-components/Utils/Functions";
import { useRouter } from "next/router";
import { withApollo } from "../lib/apollo3";
import { withAuth } from "../lib/auth";
import { withContext } from "../lib/context";
import { withGlobalComponents } from "../shared-components/ViewFragments/GlobalComponents/web";
import { withTranslation } from "../shared-components/i18n";
import AdvertiserProvider from "src/contexts/AdvertiserContext";
import { fetchSearchResults } from "src/services/search";
import { PropertyDataProvider } from "src/contexts/PropertyData/PropertyDataProvider";
import { redirect } from "next/dist/next-server/server/api-utils";
import { ContactModalProvider } from "shared-components/Contexts/ContactModal/context";
import { QUERY_PROPERTY_DETAILS } from "shared-components/GQL";
import { PropertyEmptyState } from "shared-components/Components/Property/EmptyState/web";

const propSingle = ({ __PROPERTY__ID__, data: propertyData, technicalSheeta, inactiveSimilarInfo, showEmptyState }) => {
	const { asPath } = useRouter();
	const isPrint = asPath.includes("imprimir");

	if (showEmptyState) {
		return <PropertyEmptyState data={propertyData}/>;
	}

	return (
		<PropertyDataProvider initialListing={propertyData}>
			<ContactModalProvider>
				<AdvertiserProvider>
					{isPrint ? (
						<PropertyDetailsPrint id={__PROPERTY__ID__} mode="property" />
					) : (
						<PropertyDetails
							id={__PROPERTY__ID__}
							mode="property"
							data={propertyData}
						/>
					)}
					{/* <RTBhouse page={"offer"} data={__PROPERTY__ID__} /> */}
				</AdvertiserProvider>
			</ContactModalProvider>
		</PropertyDataProvider>
	);
};

propSingle.getInitialProps = async function({ apolloClient, req, res, query }) {
	let property = await decodeHashUrl(query.hashed);
	const { main_domain } = property.configs;
	const __PROPERTY__ID__ = property.id;
	const payload = {
		rows: 1,
		params: {
			id: __PROPERTY__ID__,
		},
		page: 1,
		source: 10,
	};

	const fetchResult = await fetchSearchResults(payload, main_domain)
	const data = fetchResult?.searchFast?.data[0];
	
	let technicalSheet = null;

	if (!data) {
		
		if (res) {
			let  inactiveSimilarInfo = { 
				status: property.status,
				operation_type_id: property.operation_type_id,
				property_type_id: property.property_type_id,
				price: property.price,
				neighborhood_id: property.neighborhood_id,
				city_id: property.city_id
			}
			let showEmptyState = false;

			try {
				const { data: gqlData } = await apolloClient.query({
					query: QUERY_PROPERTY_DETAILS,
					variables: { id: __PROPERTY__ID__ },
				});
				
				const gqlProperty = gqlData?.property;
				
				let gqlPropertyData = {
					...gqlProperty,
					youtube: gqlProperty?.youtubeLink || gqlProperty?.youtube,
					title: gqlProperty?.title + " [No disponible]",
                    inactiveSimilarInfo 
				};

				
				if (!gqlProperty?.owner?.active) {
					throw new Error('owner desactivate');
				}

				if (gqlProperty?.active && !gqlProperty?.deleted) {
					res.statusCode = 202;
					showEmptyState = true;
				} 
				
				return {
					__PROPERTY__ID__,
					data: gqlPropertyData,
					showEmptyState,
					technicalSheet: null,
					inactiveSimilarInfo
				};

			} catch (e) {
				res.statusCode = 404;
			}

			return {
				__PROPERTY__ID__,
				data: null,
				showEmptyState,
				technicalSheet: null,
				inactiveSimilarInfo
			};
		} else {
			throw new Error("Data not found");
		}
	} else {
		if (property.configs.country_code === "ce3") {
			const technicalSheetCeee = [...data.technicalSheet];
			const minimumStay =
				data.seasons && !!data.seasons.length
					? `${data.seasons[0].rental_data.minimum_stay} ${
							data.seasons[0].rental_data.minimum_stay !== 1 ? "días" : "día"
					  }`
					: null;

			technicalSheetCeee.push(
				{
					field: "guests",
					text: "Huéspedes",
					value: data.guests ? data.guests.toString() : null,
				},
				{
					field: "seaview",
					text: "Vista al mar",
					value: data.seaview ? "Sí" : "No",
				},
				{
					field: "minimum_stay",
					text: "Estadía mínima",
					value: minimumStay,
				}
			);

			const filtered = technicalSheetCeee.filter(
				technical => technical.field !== "disposition_name"
			);

			technicalSheet = filtered;
		} else {
			technicalSheet = data.technicalSheet;
		}
	}

	if (data?.project?.link) {
		// La ficha canónica de una unidad es la del emprendimiento. El rebote ya existía;
		// lo que cambia acá es CÓMO (INF-1216):
		//
		// · **301 y no 307.** `redirect(res, url)` con la URL como segundo argumento cae
		//   en el default de Next, que es 307 (Temporary). Con un temporal Google no
		//   consolida: la unidad y el proyecto siguen compitiendo entre sí. La firma real
		//   es `redirect(res, statusOrUrl, url)` — pasando el status, escribe ese.
		//
		// · **Un salto en vez de dos, para la mayoría.** Iba a `/proyectos/p/<id>`, que
		//   nunca resuelve —`RouteChecker` compara el 2º tramo contra el slug del
		//   proyecto y `'p'` no matchea— así que siempre 301eaba de nuevo.
		//   `data.project.link` es la URL con slug, medida contra search-service:
		//   `"/proyectos/joy/10451"`, con barra inicial incluida.
		//   ⚠️ NO es «un salto» siempre, y conviene no prometerlo: medido sobre los 88
		//   proyectos de UY, **68 quedan en un salto y 20 siguen en dos**. Los 20 son
		//   proyectos ESPECIALES, cuya canónica en IC2 es `/proyectos/<slug>` SIN id
		//   (`Project::getLinkAttribute`), mientras que el publisher arma siempre
		//   `/proyectos/<slug>/<id>`. Eso es divergencia de emisores —familia INF-753— y
		//   se arregla en el publisher, no acá. Para esos 20 la mejora igual existe: el
		//   primer salto pasa de 307 a 301.
		//   (Ojo: el `link` del GraphQL de IC2 viene SIN barra; el que llega acá sale de
		//   search-service, que sí la trae.)
		//
		// · **Y se le pone techo al cacheo.** Un 301 sin información de frescura es
		//   cacheable heurísticamente: el browser lo persiste y del lado del server ya no
		//   se puede volver atrás. Y este 301 es el ÚNICO de la plataforma que se decide
		//   leyendo OpenSearch (`searchFast`) en vez de MySQL, o sea el mismo pipeline
		//   cuya latencia es el tema de INF-739: si una unidad se desvincula de su
		//   proyecto, o un reindex queda a medias, el redirect permanente queda grabado en
		//   el cache de cada visitante. Con una hora, un error de índice se cura solo.
		//   Google ignora `Cache-Control` para decidir si consolida un 301, así que el
		//   objetivo de SEO no se toca.
		// · **Y el destino se encodea.** Esto NO es defensa genérica: el valor viejo era
		//   `/proyectos/p/<id>`, siempre numérico y por lo tanto siempre seguro; el nuevo
		//   es texto que arma el publisher, así que el riesgo lo introduce este cambio.
		//   Node valida el header: un carácter sobre U+00FF en `Location` tira
		//   `ERR_INVALID_CHAR`, o sea **500** en vez de redirect. Y no es hipotético — el
		//   catálogo del publisher tiene U+FFFD (el de reemplazo) en 158 ubicaciones
		//   documentadas en INF-947, y preserva la `ñ` a propósito.
		//   Medido hoy sobre 353 proyectos de UY/PY/CO: cero con chars sobre U+00FF, así
		//   que el 500 todavía no pasa; 7 llevan `ñ`, que entra en latin-1 y no explota.
		//   `encodeURI` deja el ASCII intacto (el 98%) y sólo toca lo que haría fallar;
		//   verificado en producción que la forma cruda y la encodeada dan exactamente el
		//   mismo resultado.
		res.setHeader("Cache-Control", "public, max-age=3600");
		redirect(res, 301, encodeURI(data.project.link));
	}

	if(property.status === "deactivated") {
		data.active = false;
		data.inactiveSimilarInfo = {
			status: property.status,
			operation_type_id: property.operation_type_id,
			property_type_id: property.property_type_id,
			price: property.price,
			neighborhood_id: property.neighborhood_id,
			city_id: property.city_id
		}
	}

	return {
		__PROPERTY__ID__,
		data,
		technicalSheet,
		ConfigContextInitialState: property?.configs,
	};
};

export default withApollo(
	withContext(withTranslation(withAuth(withGlobalComponents(propSingle))), {
		withDummyContext: false,
		withFiltersContext: true,
		withConfigContext: true,
		withThemeContext: true,
	})
);

